'use client'

import { useEffect, useState } from 'react'

import { useMachine } from '@xstate/react'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertCircle, ArrowLeft, CheckCircle, Clock } from 'lucide-react'

import {
  UserProfile,
  WorkshopInstance,
  WorkshopPreference,
  workshopApi,
} from '../../lib/workshop-api'
import {
  getStateDisplayName,
  getStepProgress,
  workshopRegistrationMachine,
} from '../../lib/workshop-state'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Progress } from '../ui/progress'
import { ConfirmationPage } from './ConfirmationPage'
import { PaymentForm } from './PaymentForm'
import { ReviewSelection } from './ReviewSelection'
import { UserInfoForm } from './UserInfoForm'
import { WaitlistStatus } from './WaitlistStatus'
import { WorkshopSelection } from './WorkshopSelection'

interface RegistrationWizardProps {
  workshopId?: string
  onComplete?: (registrationId: string) => void
  onCancel?: () => void
}

export function RegistrationWizard({ workshopId, onComplete, onCancel }: RegistrationWizardProps) {
  const [state, send] = useMachine(workshopRegistrationMachine, {
    services: {
      startRegistration: async context => {
        if (!context.userEmail || !context.userProfile || !context.selectedWorkshops.length) {
          throw new Error('Missing required data')
        }

        const response = await workshopApi.startRegistration({
          email: context.userEmail,
          preferences: context.selectedWorkshops,
          userProfile: context.userProfile,
        })

        if (!response.success) {
          throw new Error(response.error?.message || 'Registration failed')
        }

        return response.data
      },

      createPaymentIntent: async context => {
        if (
          !context.registrationId ||
          !context.selectedWorkshops[0]?.workshopInstanceId ||
          !context.paymentMethod
        ) {
          throw new Error('Missing payment data')
        }

        const response = await workshopApi.createPaymentIntent(
          context.registrationId,
          context.selectedWorkshops[0].workshopInstanceId,
          context.paymentMethod
        )

        if (!response.success) {
          throw new Error(response.error?.message || 'Payment setup failed')
        }

        return response.data
      },

      joinWaitlist: async context => {
        if (!context.registrationId || !context.selectedWorkshops.length) {
          throw new Error('Missing waitlist data')
        }

        const response = await workshopApi.joinWaitlist(
          context.registrationId,
          context.selectedWorkshops
        )

        if (!response.success) {
          throw new Error(response.error?.message || 'Failed to join waitlist')
        }

        return response.data
      },

      acceptSpotOffer: async context => {
        if (!context.registration?.id) {
          throw new Error('No registration found')
        }

        // This would need the waitlist entry ID from the registration data
        const waitlistEntryId = context.registration.contextData?.waitlistEntryId
        if (!waitlistEntryId) {
          throw new Error('No waitlist entry found')
        }

        const response = await workshopApi.acceptSpotOffer(
          waitlistEntryId,
          context.paymentMethod || 'stripe'
        )

        if (!response.success) {
          throw new Error(response.error?.message || 'Failed to accept spot')
        }

        return response.data
      },

      cancelRegistration: async context => {
        if (!context.registrationId) {
          throw new Error('No registration to cancel')
        }

        const response = await workshopApi.cancelRegistration(context.registrationId)

        if (!response.success) {
          throw new Error(response.error?.message || 'Failed to cancel registration')
        }

        return response.data
      },
    },
  })

  // Real-time updates for registration status
  useEffect(() => {
    if (!state.context.registrationId) return

    const eventSource = workshopApi.createRegistrationStream(state.context.registrationId)

    eventSource.onmessage = event => {
      try {
        const registration = JSON.parse(event.data)
        send({ type: 'REGISTRATION_UPDATE', registration })
      } catch (error) {
        console.error('Failed to parse registration update:', error)
      }
    }

    return () => eventSource.close()
  }, [state.context.registrationId, send])

  // Handle completion
  useEffect(() => {
    if (state.matches('confirmed') && state.context.registrationId && onComplete) {
      onComplete(state.context.registrationId)
    }
  }, [state, onComplete])

  const handleStartRegistration = () => {
    send({ type: 'START_REGISTRATION' })
  }

  const handleUserInfoSubmit = (email: string, profile: UserProfile) => {
    send({ type: 'SUBMIT_BASIC_INFO', email, profile })
  }

  const handleWorkshopSelection = (preferences: WorkshopPreference[]) => {
    // Update preferences as they're selected
    preferences.forEach(preference => {
      send({ type: 'ADD_WORKSHOP_PREFERENCE', preference })
    })
  }

  const handlePreferencesSubmit = () => {
    send({ type: 'SUBMIT_PREFERENCES' })
  }

  const handleConfirmSelection = () => {
    send({ type: 'CONFIRM_SELECTION' })
  }

  const handlePaymentMethodChoice = (method: 'stripe' | 'xero') => {
    send({ type: 'CHOOSE_PAYMENT_METHOD', method })
  }

  const handlePaymentSuccess = () => {
    send({ type: 'PAYMENT_SUCCEEDED', registrationId: state.context.registrationId || '' })
  }

  const handlePaymentFailure = (error: string) => {
    send({ type: 'PAYMENT_FAILED', error })
  }

  const handleJoinWaitlist = () => {
    send({ type: 'JOIN_WAITLIST' })
  }

  const handleAcceptSpotOffer = () => {
    send({ type: 'ACCEPT_SPOT_OFFER' })
  }

  const handleDeclineSpotOffer = () => {
    send({ type: 'DECLINE_SPOT_OFFER' })
  }

  const handleEditPreferences = () => {
    send({ type: 'EDIT_PREFERENCES' })
  }

  const handleEditInfo = () => {
    send({ type: 'EDIT_INFO' })
  }

  const handleRetry = () => {
    send({ type: 'RETRY' })
  }

  const handleCancel = () => {
    send({ type: 'CANCEL_REGISTRATION' })
    if (onCancel) onCancel()
  }

  const currentStateName = state.value.toString()
  const progress = getStepProgress(state.context.currentStep)

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div className="space-y-4 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kinetic-outline">
          {getStateDisplayName(currentStateName)}
        </p>

        {/* Progress Bar */}
        {!state.matches('landing') && (
          <div className="mx-auto w-full max-w-md">
            <Progress
              value={progress}
              className="h-2 rounded-none bg-kinetic-surface-container-highest [&>div]:bg-kinetic-primary-container [&>div]:dark:bg-kinetic-primary-container"
            />
            <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-kinetic-on-surface-variant">
              Step {state.context.currentStep} of 6
            </p>
          </div>
        )}
      </div>

      {/* Error Messages */}
      {state.context.errors.length > 0 && (
        <Card className="border-red-500/40 rounded-none border bg-kinetic-surface-container-low p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="mt-0.5 h-5 w-5 text-red-500" />
            <div className="space-y-2">
              {state.context.errors.map((error, index) => (
                <p key={index} className="text-red-700 dark:text-red-300">
                  {error}
                </p>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Success Messages */}
      {state.context.notifications.length > 0 && (
        <Card className="border-kinetic-primary-container/35 rounded-none border bg-kinetic-surface-container-low p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="mt-0.5 h-5 w-5 text-green-500" />
            <div className="space-y-2">
              {state.context.notifications.map((notification, index) => (
                <p key={index} className="text-kinetic-on-surface-variant">
                  {notification}
                </p>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Main Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStateName}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Landing */}
          {state.matches('landing') && (
            <Card className="border-kinetic-outline-variant/30 space-y-6 rounded-none border bg-kinetic-surface-container-low p-8 text-center">
              <div className="space-y-4">
                <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
                  Ready to register?
                </h2>
                <p className="text-kinetic-on-surface-variant">
                  Join our expert-led workshops and accelerate your data engineering skills.
                </p>
              </div>
              <Button onClick={handleStartRegistration} variant="kinetic" size="lg">
                Start Registration
              </Button>
            </Card>
          )}

          {/* Collecting Basic Info */}
          {state.matches('collectingBasicInfo') && (
            <UserInfoForm
              onSubmit={handleUserInfoSubmit}
              initialData={{
                email: state.context.userEmail || '',
                profile: state.context.userProfile,
              }}
              loading={state.context.isLoading}
            />
          )}

          {/* Selecting Preferences */}
          {state.matches('selectingPreferences') && (
            <WorkshopSelection
              workshopId={workshopId}
              selectedPreferences={state.context.selectedWorkshops}
              onSelectionChange={handleWorkshopSelection}
              onSubmit={handlePreferencesSubmit}
              loading={state.context.isLoading}
            />
          )}

          {/* Reviewing Selection */}
          {state.matches('reviewingSelection') && (
            <ReviewSelection
              userEmail={state.context.userEmail || ''}
              userProfile={state.context.userProfile}
              selectedWorkshops={state.context.selectedWorkshops}
              onConfirm={handleConfirmSelection}
              onEditInfo={handleEditInfo}
              onEditPreferences={handleEditPreferences}
            />
          )}

          {/* Payment Required */}
          {state.matches('paymentRequired') && (
            <PaymentForm
              registration={state.context.registration}
              paymentClientSecret={state.context.paymentClientSecret}
              onPaymentMethodSelect={handlePaymentMethodChoice}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentFailure={handlePaymentFailure}
              onJoinWaitlist={handleJoinWaitlist}
              loading={state.context.isLoading}
            />
          )}

          {/* Waitlist States */}
          {(state.matches('waitlistOptions') ||
            state.matches('onWaitlist') ||
            state.matches('spotOffered')) && (
            <WaitlistStatus
              registration={state.context.registration}
              waitlistPosition={state.context.waitlistPosition}
              spotOfferExpiry={state.context.spotOfferExpiry}
              onJoinWaitlist={handleJoinWaitlist}
              onAcceptSpot={handleAcceptSpotOffer}
              onDeclineSpot={handleDeclineSpotOffer}
              onEditPreferences={handleEditPreferences}
              loading={state.context.isLoading}
            />
          )}

          {/* Confirmed */}
          {state.matches('confirmed') && (
            <ConfirmationPage registration={state.context.registration} onCancel={handleCancel} />
          )}

          {/* Error State */}
          {state.matches('errorState') && (
            <Card className="border-red-500/40 space-y-6 rounded-none border bg-kinetic-surface-container-low p-8 text-center">
              <div className="space-y-4">
                <AlertCircle className="mx-auto h-16 w-16 text-red-400" />
                <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
                  Something went wrong
                </h2>
                <p className="text-kinetic-on-surface-variant">
                  We encountered an error while processing your registration.
                </p>
              </div>
              <div className="flex justify-center space-x-4">
                {state.context.retryCount < 3 && (
                  <Button
                    onClick={handleRetry}
                    variant="outline"
                    className="border-kinetic-outline-variant/50 rounded-none text-kinetic-on-surface hover:bg-kinetic-surface-container-high"
                  >
                    Try Again
                  </Button>
                )}
                <Button
                  onClick={handleEditPreferences}
                  variant="outline"
                  className="border-kinetic-outline-variant/50 rounded-none text-kinetic-on-surface hover:bg-kinetic-surface-container-high"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Edit Preferences
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="border-red-500/50 hover:bg-red-950/30 rounded-none text-red-400"
                >
                  Cancel Registration
                </Button>
              </div>
            </Card>
          )}

          {/* Loading States */}
          {(state.matches('submittingRegistration') ||
            state.matches('checkingAvailability') ||
            state.matches('joiningWaitlist') ||
            state.matches('acceptingSpotOffer') ||
            state.matches('cancelling')) && (
            <Card className="border-kinetic-outline-variant/30 space-y-6 rounded-none border bg-kinetic-surface-container-low p-8 text-center">
              <div className="space-y-4">
                <Clock className="mx-auto h-16 w-16 animate-spin text-kinetic-primary-container" />
                <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
                  Processing…
                </h2>
                <p className="text-kinetic-on-surface-variant">
                  {getStateDisplayName(currentStateName)}
                </p>
              </div>
            </Card>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer Actions */}
      {!state.matches('landing') &&
        !state.matches('confirmed') &&
        !state.matches('cancelled') &&
        !state.context.isLoading && (
          <div className="border-kinetic-outline-variant/30 flex justify-between border-t pt-6">
            <Button
              variant="ghost"
              onClick={handleCancel}
              className="text-kinetic-on-surface-variant hover:bg-kinetic-surface-container-high hover:text-white"
            >
              Cancel Registration
            </Button>

            {state.context.registrationId && (
              <p className="flex items-center font-mono text-[10px] uppercase tracking-wider text-kinetic-outline">
                ID {state.context.registrationId.slice(0, 8)}…
              </p>
            )}
          </div>
        )}
    </div>
  )
}
